import clsx from 'clsx'
import React, { ComponentPropsWithRef, useEffect, useRef } from 'react'

import styles from './ProductTileWrapper.module.scss'

export const ProductTileWrapper = ({
  children,
  className,
  maxColumns = 4,
  showMore = false,
  ...props
}: ComponentPropsWithRef<'div'> & {
  maxColumns?: 4 | 8
  showMore?: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)

  // toggles data-first-tile-in-view on the grid; tile styles use it to
  // (re)play the scroll-hint animation. The thresholds are deliberately
  // asymmetric (a hysteresis latch): set at >= 60% visible, cleared only
  // once fully out of view. Clearing below 60% instead would cancel the
  // animation mid-view (slides snap back) and replay it on every small
  // scroll around the threshold.
  useEffect(() => {
    const wrapper = ref.current
    const firstTile = wrapper?.firstElementChild
    if (!wrapper || !firstTile || typeof IntersectionObserver === 'undefined') {
      return undefined
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio >= 0.6) {
          wrapper.setAttribute('data-first-tile-in-view', 'true')
        } else if (!entry.isIntersecting) {
          wrapper.removeAttribute('data-first-tile-in-view')
        }
      })
    }, { threshold: [0, 0.6] })
    observer.observe(firstTile)
    return () => observer.disconnect()
  }, [children])

  return (
    <div
      className={clsx(
        styles.productTileWrapper,
        maxColumns === 4 && styles.maxFour,
        maxColumns === 8 && styles.maxEight,
        showMore && styles.showMore,
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  )
}
